const BEIJING_TIMEZONE = "Asia/Shanghai";

function normalizeIsoLike(value: string): string {
  return value.replace("T", " ").trim();
}

function hasExplicitTimezone(value: string): boolean {
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
}

function extractDateParts(value: string): {
  date: string;
  time: string;
  hasSeconds: boolean;
} | null {
  const normalized = normalizeIsoLike(value);
  const match = normalized.match(
    /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})(?::(\d{2}))?/
  );
  if (!match) return null;
  return {
    date: match[1],
    time: match[3] ? `${match[2]}:${match[3]}` : match[2],
    hasSeconds: !!match[3],
  };
}

function formatDate(date: Date, options: Intl.DateTimeFormatOptions) {
  return date.toLocaleString("zh-CN", {
    timeZone: BEIJING_TIMEZONE,
    hour12: false,
    ...options,
  });
}

export function formatBeijingDateTime(
  value: string | Date | null | undefined,
  options?: { withSeconds?: boolean }
): string {
  if (!value) return "";
  if (typeof value === "string" && !hasExplicitTimezone(value)) {
    const extracted = extractDateParts(value);
    if (extracted) {
      return `${extracted.date} ${
        options?.withSeconds
          ? extracted.hasSeconds
            ? extracted.time
            : `${extracted.time}:00`
          : extracted.time.slice(0, 5)
      }`;
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === "string" ? normalizeIsoLike(value).slice(0, 16) : "";
  }
  return formatDate(date, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: options?.withSeconds ? "2-digit" : undefined,
  }).replace(/\//g, "-");
}

export function formatBeijingDate(
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!value) return "";
  if (typeof value === "string" && !hasExplicitTimezone(value)) {
    const extracted = extractDateParts(value);
    if (extracted) return extracted.date;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return formatDate(date, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...options,
  }).replace(/\//g, "-");
}

export function formatBeijingTime(
  value: string | Date | null | undefined,
  options?: { withSeconds?: boolean }
): string {
  if (!value) return "";
  if (typeof value === "string" && !hasExplicitTimezone(value)) {
    const extracted = extractDateParts(value);
    if (extracted) {
      return options?.withSeconds
        ? extracted.hasSeconds
          ? extracted.time
          : `${extracted.time}:00`
        : extracted.time.slice(0, 5);
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return formatDate(date, {
    hour: "2-digit",
    minute: "2-digit",
    second: options?.withSeconds ? "2-digit" : undefined,
  });
}

export function formatBeijingMonthDayTime(
  value: string | Date | null | undefined
): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return formatBeijingDateTime(value).slice(5);
  }
  return formatDate(date, {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).replace(/\//g, "-");
}

export function formatBeijingLongDate(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return formatDate(date, {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  });
}
